import { z } from "zod";

// ============================================================================
// Structured audit log — `AuditEvent` (backend: midship_core.models.heretic.
// audit_event.AuditEvent). Backend serializes via Pydantic
// `alias_generator=to_camel` (see `controlSession.ts:58-60`), so wire format
// is camelCase. Field names below intentionally match the BE JSON.
//
// Exactly one of the eight payload fields (`run`/`change`/`override`/
// `document`/`citation`/`lock`/`approval`/`sample`) is set per event,
// selected by `eventType`; the rest are absent. All payload schema fields
// are optional for defensive parsing.
// ============================================================================

export const auditEventTypeSchema = z.enum([
  "test_created",
  "session_renamed",
  "session_deleted",
  "template_changed",
  "control_config_edited",
  "test_run",
  "sample_rerun",
  "cell_rerun",
  "column_rerun",
  "documents_rematched",
  "manual_override",
  "support_data_cell_edited",
  "walkthrough_step_edited",
  "walkthrough_verdict_edited",
  "walkthrough_citation_imported",
  "reperformance_step_added",
  "reperformance_step_edited",
  "reperformance_step_deleted",
  "sample_row_added",
  "sample_row_edited",
  "sample_row_deleted",
  "sample_upload_confirmed",
  "citation_added",
  "citation_updated",
  "citation_deleted",
  "citation_marked_key",
  "result_locked",
  "result_unlocked",
  "result_deleted",
  "evidence_added",
  "evidence_removed",
  "submitted_for_review",
  "approval_requested",
  "approval_approved",
  "approval_rejected",
  // Workspace-level: names no control or test.
  "member_removed",
]);

export type AuditEventType = z.infer<typeof auditEventTypeSchema>;

/** One sample's cell value change from a rerun that targeted a column. */
export const runCellChangeSchema = z.object({
  sampleLabel: z.string().nullable().optional(),
  before: z.string().nullable().optional(),
  after: z.string().nullable().optional(),
});

export type RunCellChange = z.infer<typeof runCellChangeSchema>;

export const runPayloadSchema = z.object({
  sampleCount: z.number().int().nullable().optional(),
  instructions: z.string().nullable().optional(),
  targetColumnIds: z.array(z.string()).nullable().optional(),
  /** Present only on reruns that targeted a single support column. Empty
   * array = diffs were computed and no value moved; null/absent = diffs
   * were not applicable to this run. */
  cellChanges: z.array(runCellChangeSchema).nullable().optional(),
});

export type RunPayload = z.infer<typeof runPayloadSchema>;

/** One field's before/after, so each change can be redlined on its own. */
export const fieldChangeSchema = z.object({
  field: z.string(),
  before: z.string().nullable().optional(),
  after: z.string().nullable().optional(),
});

export type FieldChange = z.infer<typeof fieldChangeSchema>;

/** A before/after text change; FE renders the redline diff from these. */
export const changePayloadSchema = z.object({
  target: z.string().nullable().optional(),
  before: z.string().nullable().optional(),
  after: z.string().nullable().optional(),
  fieldChanges: z.array(fieldChangeSchema).nullable().optional(),
});

export type ChangePayload = z.infer<typeof changePayloadSchema>;

export const overridePayloadSchema = z.object({
  outcomeBefore: z.string().nullable().optional(),
  outcomeAfter: z.string().nullable().optional(),
  conclusionBefore: z.string().nullable().optional(),
  conclusionAfter: z.string().nullable().optional(),
  fieldChanges: z.array(fieldChangeSchema).optional(),
});

export type OverridePayload = z.infer<typeof overridePayloadSchema>;

export const documentPayloadSchema = z.object({
  documentNames: z.array(z.string()).optional(),
  /** Closed enum backend-side, but a newly added scope must degrade this
   * field to undefined — not fail the per-event parse and silently drop the
   * whole event from an audit surface. */
  scope: z
    .enum(["session", "sample", "control", "supplementary"])
    .optional()
    .catch(undefined),
  detail: z.string().nullable().optional(),
});

export type DocumentPayload = z.infer<typeof documentPayloadSchema>;

export const citationPayloadSchema = z.object({
  /** The auditor's short summary — why this content was cited. Older rows
   * may carry an encoded locator string here instead; render as-is. */
  summary: z.string().nullable().optional(),
  isKey: z.boolean().nullable().optional(),
  /** The cited document's file name, resolved at emission; absent on older
   * rows. */
  documentName: z.string().nullable().optional(),
  /** The step the citation hangs off ("Reperformance Step 2"), point-in-time;
   * absent for support-/eval-linked citations and older rows. */
  location: z.string().nullable().optional(),
});

export type CitationPayload = z.infer<typeof citationPayloadSchema>;

export const lockPayloadSchema = z.object({
  lockedByUserId: z.string().nullable().optional(),
});

export type LockPayload = z.infer<typeof lockPayloadSchema>;

export const approvalPayloadSchema = z.object({
  requestId: z.string().optional(),
  comment: z.string().nullable().optional(),
});

export type ApprovalPayload = z.infer<typeof approvalPayloadSchema>;

export const samplePayloadSchema = z.object({
  sampleCount: z.number().int().nullable().optional(),
  fieldName: z.string().nullable().optional(),
  before: z.string().nullable().optional(),
  after: z.string().nullable().optional(),
});

export type SamplePayload = z.infer<typeof samplePayloadSchema>;

/** The member an admin acted on; the admin is `triggeredBy*`. */
export const memberPayloadSchema = z.object({
  userId: z.string().optional(),
  /** Point-in-time address of the account. */
  email: z.string().optional(),
});

export type MemberPayload = z.infer<typeof memberPayloadSchema>;

export const auditEventSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string(),
  eventType: auditEventTypeSchema,
  title: z.string(),
  performedBy: z.enum(["user", "ai_system"]),

  controlId: z.string().nullable().optional(),
  controlSessionId: z.string().nullable().optional(),
  attributeTestColumnId: z.string().nullable().optional(),
  attributeLabel: z.string().nullable().optional(),
  sourceRowId: z.string().nullable().optional(),
  sampleLabel: z.string().nullable().optional(),
  controlName: z.string().nullable().optional(),
  testName: z.string().nullable().optional(),
  triggeredByUserId: z.string().nullable().optional(),
  triggeredByEmail: z.string().nullable().optional(),

  run: runPayloadSchema.nullable().optional(),
  change: changePayloadSchema.nullable().optional(),
  override: overridePayloadSchema.nullable().optional(),
  document: documentPayloadSchema.nullable().optional(),
  citation: citationPayloadSchema.nullable().optional(),
  lock: lockPayloadSchema.nullable().optional(),
  approval: approvalPayloadSchema.nullable().optional(),
  sample: samplePayloadSchema.nullable().optional(),
  member: memberPayloadSchema.nullable().optional(),

  /** Read-time enrichment from the workspace feed: what the control/session
   * are called NOW. `controlName`/`testName` above stay point-in-time. */
  currentControlName: z.string().nullable().optional(),
  currentTestName: z.string().nullable().optional(),
  /** Tests currently hanging off this event's control, sent only for
   * control-level events that carry no session of their own. Their presence
   * separates a child control from a template, which has none. */
  currentControlTestNames: z.array(z.string()).optional().catch(undefined),

  createdAt: z.string(),
});

export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditEventFeedSchema = z.object({
  events: z.array(auditEventSchema),
  nextCursor: z.string().nullable(),
});

export type AuditEventFeed = z.infer<typeof auditEventFeedSchema>;
